import { z } from "zod";
/** Public English dictionary API (Wiktionary-backed); used only to fill POS + IPA when OG/metadata lacks them. */
const DICTIONARY_API_BASE = "https://api.dictionaryapi.dev/api/v2/entries/en";
const MAX_HEADWORD_LEN = 80;
const MAX_PHONETIC_LEN = 120;
export const DICTIONARY_LEXICAL_HINTS_FETCH_TIMEOUT_MS = 8000;
const MeaningSchema = z.object({
    partOfSpeech: z.string().max(40),
});
const EntrySchema = z.object({
    phonetic: z.string().max(MAX_PHONETIC_LEN).optional(),
    phonetics: z.array(z.object({ text: z.string().max(MAX_PHONETIC_LEN).optional() })).max(25).optional(),
    meanings: z.array(MeaningSchema).max(40).optional(),
});
const DictionaryApiResponseSchema = z.array(EntrySchema).max(5);
/**
 * Normalizes a headword for the public dictionary API path segment (English lemmas only).
 */
export function normalizeHeadwordForDictionaryApi(headword) {
    const q = headword
        .trim()
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/-/g, " ")
        .replace(/\s+/g, " ");
    if (q.length < 2 || q.length > MAX_HEADWORD_LEN)
        return undefined;
    if (/^[a-z\s']+$/i.test(q) === false)
        return undefined;
    return q;
}
export function parseDictionaryApiResponse(data) {
    const parsed = DictionaryApiResponseSchema.safeParse(data);
    if (parsed.success === false)
        return undefined;
    const [first] = parsed.data;
    if (!first)
        return undefined;
    const rawPos = first.meanings?.[0]?.partOfSpeech?.trim().toLowerCase();
    const partOfSpeech = rawPos && rawPos.length > 0 ? rawPos : undefined;
    const rawPhone = first.phonetic?.trim() ||
        first.phonetics?.map((p) => p.text?.trim()).find((t) => Boolean(t && t.length > 0));
    const phonetic = rawPhone ? formatPhoneticForCard(rawPhone) : undefined;
    if (!partOfSpeech && !phonetic)
        return undefined;
    return { partOfSpeech, phonetic };
}
/** Drops wrapping slashes so the meta line matches dictionary-card typography (MW-style respelling vs IPA). */
export function formatPhoneticForCard(raw) {
    let t = raw.trim();
    if (t.length > MAX_PHONETIC_LEN) {
        t = t.slice(0, MAX_PHONETIC_LEN - 1).trimEnd() + "…";
    }
    if (t.startsWith("/") && t.endsWith("/") && t.length > 2) {
        return t.slice(1, -1).trim();
    }
    return t;
}
export async function fetchDictionaryApiLexicalHints(normalizedWord, signal) {
    if (signal.aborted)
        return undefined;
    const url = `${DICTIONARY_API_BASE}/${encodeURIComponent(normalizedWord)}`;
    const controller = new AbortController();
    const onParentAbort = () => {
        controller.abort();
    };
    signal.addEventListener("abort", onParentAbort);
    const killTimer = setTimeout(() => {
        controller.abort();
    }, DICTIONARY_LEXICAL_HINTS_FETCH_TIMEOUT_MS);
    try {
        const res = await fetch(url, {
            signal: controller.signal,
            headers: { Accept: "application/json" },
        });
        if (res.ok === false)
            return undefined;
        const data = await res.json();
        return parseDictionaryApiResponse(data);
    }
    catch {
        return undefined;
    }
    finally {
        clearTimeout(killTimer);
        signal.removeEventListener("abort", onParentAbort);
    }
}
